// Wire codec for filter conditions (Saved-Filter Builder, PR 1).
//
// CONSUMER: shared (Workers runtime + web via @shared + tests).
//
// The OS already has a stable filter wire format: a repeated `f` query
// param holding `key:value` (first colon splits; chunks may also be
// comma-joined). Every existing quick filter, saved view, and bookmark
// uses it, and it stays valid FOREVER — this codec parses it into the
// new model via the registry's legacy map.
//
// Conditions with operators use a second chunk shape that coexists in
// the same `f` param:
//
//     field~op~value[~value2…]        e.g.  age~between~55~65
//                                           fit~in~ideal_fit~typical
//                                           city~contains~gil
//
// DECISION — why `~` and not an extended `key:value`: the legacy
// parser splits on the FIRST colon, so any colon-based extension would
// be mis-read as a legacy key by older parsers. `~` is URL-unreserved
// (RFC 3986), never appears in legacy keys, and a legacy parser simply
// ignores a tilde chunk (no colon → skipped), so old and new chunks
// degrade gracefully past each other.
//
// Escaping: `%`, `~`, and `,` inside value segments are percent-style
// escaped by the codec itself (the comma matters because routes split
// chunks on commas before this codec sees them).
//
// Ambiguity guard: a chunk containing `~` is only treated as v2 when
// its second segment is a known operator; otherwise the whole chunk
// falls back to legacy `key:value` interpretation. A legacy value that
// happens to contain a tilde therefore still parses correctly unless
// it ALSO mimics an operator name — accepted residual risk, documented
// and tested.

import {
  isFilterOp,
  MAX_FILTER_CONDITIONS,
  type FilterCondition,
  type FilterSet,
  FILTER_MODEL_VERSION,
} from "./filter-model";
import { legacyToCondition, type FilterEntity } from "./field-registry";

// ─── Segment escaping ────────────────────────────────────────────────

export function escapeSegment(s: string): string {
  // Order matters: escape the escape character first. Parentheses are
  // escaped too (audit fix 2026-06-10) — encodeURIComponent leaves
  // them bare, and the Stew bubble linkifier stops an URL at `)`,
  // which truncated deep links whose values contained one.
  return s
    .replace(/%/g, "%25")
    .replace(/~/g, "%7E")
    .replace(/,/g, "%2C")
    .replace(/\(/g, "%28")
    .replace(/\)/g, "%29");
}

export function unescapeSegment(s: string): string {
  // Reverse order: unescape the escape character last.
  return s
    .replace(/%28/gi, "(")
    .replace(/%29/gi, ")")
    .replace(/%7E/gi, "~")
    .replace(/%2C/gi, ",")
    .replace(/%25/g, "%");
}

// ─── Serialization ───────────────────────────────────────────────────

/** One condition → one `f` chunk in the v2 tilde shape. */
export function serializeCondition(c: FilterCondition): string {
  const segments = [c.field, c.op, ...c.values.map(escapeSegment)];
  return segments.join("~");
}

/** A FilterSet → the list of `f` chunks (one per condition). */
export function serializeFilterSet(set: FilterSet): string[] {
  return set.conditions.map(serializeCondition);
}

// ─── Parsing ─────────────────────────────────────────────────────────

export interface ParsedFilterParams {
  set: FilterSet;
  /**
   * Legacy `key:value` pairs the registry could not map (unknown keys
   * or malformed values). Preserved verbatim so callers can pass them
   * through untouched — a URL never loses information by round-
   * tripping through this codec.
   */
  unknown: { key: string; value: string }[];
}

/**
 * Parse raw `f` chunks (already URL-decoded, already comma-split by
 * the route — but we re-split commas defensively for callers that
 * hand us the joined form) into a FilterSet plus unmapped leftovers.
 */
export function parseFilterParams(
  entity: FilterEntity,
  rawChunks: readonly string[],
): ParsedFilterParams {
  const conditions: FilterCondition[] = [];
  const unknown: { key: string; value: string }[] = [];

  const push = (c: FilterCondition) => {
    if (conditions.length < MAX_FILTER_CONDITIONS) conditions.push(c);
  };

  for (const raw of rawChunks) {
    if (!raw) continue;
    for (const piece of raw.split(",")) {
      const chunk = piece.trim();
      if (!chunk) continue;

      const v2 = tryParseV2Chunk(chunk);
      if (v2) {
        push(v2);
        continue;
      }

      // Legacy `key:value` (first colon splits, value may contain colons).
      const idx = chunk.indexOf(":");
      if (idx === -1) continue; // unintelligible — same as server parser
      const key = chunk.slice(0, idx);
      const value = chunk.slice(idx + 1);
      if (!key || !value) continue;

      const converted = legacyToCondition(entity, key, value);
      if (converted) {
        push(converted);
      } else {
        unknown.push({ key, value });
      }
    }
  }

  return {
    set: { version: FILTER_MODEL_VERSION, match: "all", conditions },
    unknown,
  };
}

/**
 * Attempt to read a chunk as the v2 tilde shape. Returns null when
 * the chunk isn't v2 (no tilde, too few segments, or an unknown op in
 * the operator slot — the ambiguity guard).
 */
export function tryParseV2Chunk(chunk: string): FilterCondition | null {
  if (!chunk.includes("~")) return null;
  const segments = chunk.split("~");
  if (segments.length < 3) return null;
  const [field, op, ...rawValues] = segments;
  if (!field || !isFilterOp(op)) return null;
  const values = rawValues.map(unescapeSegment);
  if (values.length === 0 || values.some((v) => v.length === 0)) return null;
  return { field, op, values };
}
